/**
 * A PRESENTATIONAL COPY of the card parents see in MyPymble. The editor's
 * "Preview permission card" window now draws the event modal instead
 * (EventModalPreview), which reuses the row pieces exported below.
 *
 * The portal's card is now a SUMMARY: image, title, date, location and the
 * description cut to three lines, with a View button and no decision controls.
 * Medical, terms, travel, the lock warning, the external-form route and
 * Approve / Decline all moved into the modal. The row pieces below are drawn
 * there only.
 *
 * Source: my-pymble-portal, src/components/approvals/ (read only, at portal
 * HEAD on feature/approvals-actions-in-modal, 2026-10-23, 5e03abc91):
 *   ApprovalItemCard.tsx (+ .css), ApprovalItem.css, ApprovalTermsAndConds.tsx,
 *   ApprovalMedicalConfirmation.tsx (+ .css), ApprovalTransportAck.tsx (+ .css),
 *   EventAlert.tsx (+ .css), utils/eventTransport.ts.
 * Wording is the English strings in public/locales/en/parents.json
 * ("permissions.*"). Markup and class names are kept so the two cards read the
 * same; the CSS lives in approvalCardPreview.css.
 *
 * What is NOT carried over, on purpose: useStudentProfile, useDayUv and the UV
 * hat, launchExternal, onUpdate, the approve / decline calls and the confirm
 * dialogs, the toasts, the decided status band and border (Approved,
 * Declined), and the event detail modal behind a card tap (see
 * EventModalPreview, which the preview window now draws). The "No response"
 * band is drawn for a past event, as the portal does for one nobody answered.
 * Nothing here submits, navigates or fetches, so every control is inert. The student
 * and parent are invented and the card says so.
 *
 * Everything drawn comes from the event's draft; only the student ("Sample
 * Student, Year 7") is sample data. The portal's "Needs Form" button (its
 * FormUrl) has no field on the draft, so it is not drawn, and the modal's
 * "Medical Authority" button is drawn disabled because its link
 * (Urls.MedicalAuth) is not available here.
 *
 * Copy, not import: drift is possible. When the portal card changes, re-read
 * the files above and update this one.
 */
import React from "react";
import {
	IonBadge,
	IonButton,
	IonCard,
	IonCardContent,
	IonCheckbox,
	IonHeader,
	IonIcon,
	IonItem,
	IonList,
	IonModal,
	IonText,
} from "@ionic/react";
import {
	calendarOutline,
	close,
	downloadOutline,
	locationOutline,
	timeOutline,
} from "ionicons/icons";
import { CCRichText } from "@pymblecitizencoders/cc-ui/editor";
import { EventDoc } from "../../types/event";
import {
	APPROVAL_TEXT as TEXT,
	SAMPLE_STUDENT,
	SAMPLE_YEAR,
	buildApprovalCardModel,
} from "../../utils/permissionCard";
import { asHtml } from "../../utils/richText";
import "./approvalCardPreview.css";

/* The strings are in utils/permissionCard.ts (APPROVAL_TEXT), shared with the
   event modal preview; see the note there on which are unverified. */

/* Row pieces the card and the event modal draw the same way (the portal's
   ApprovalMedicalConfirmation, ApprovalTermsAndConds, ApprovalTransportAck and
   the terms EventAlert). Each is the row's inner markup only: the card wraps it
   in an IonItem, the modal in a grid column. */
export const MedicalRow: React.FC = () => (
	<div className="med-details-container">
		<IonCheckbox className="med-detail-check" disabled />
		<IonText className="internal-medical-details ion-text-nowrap">
			<IonText className="medical-details">{TEXT.medicalDetails}</IonText>
			{TEXT.areCorrect}
		</IonText>
		<IonButton
			className="view-med-details-btn ion-text-capitalize"
			color="success"
			fill="outline"
			disabled
		>
			{TEXT.medicalAuthority}
			<IonIcon slot="end" icon={downloadOutline} />
		</IonButton>
	</div>
);

export const TermsRow: React.FC<{ onView: () => void }> = ({ onView }) => (
	<div className="term-cond-row">
		<IonCheckbox className="term-cond-check" disabled />
		<button type="button" className="term-cond-link" onClick={onView}>
			{TEXT.termsLink}
		</button>
	</div>
);

export const TransportAckRow: React.FC<{ text: string }> = ({ text }) => (
	<div className="transport-ack-row">
		<IonCheckbox
			className="transport-ack-check"
			labelPlacement="end"
			justify="start"
			disabled
		>
			{text}
		</IonCheckbox>
	</div>
);

/** The portal's EventAlert for the terms, trimmed to reading them. */
export const TermsModal: React.FC<{
	open: boolean;
	onClose: () => void;
	termsHtml: string | null;
}> = ({ open, onClose, termsHtml }) => (
	<IonModal
		isOpen={open}
		onDidDismiss={onClose}
		className="tc-alert-popup event-alert-popup alert-popup app-modal tnc_modal em-approval-terms"
	>
		<IonHeader className="event-detail-header">
			<div className="event-header-content">
				<h3 className="event-title">{TEXT.termsHeader}</h3>
				<IonButton
					fill="clear"
					color="medium"
					size="small"
					aria-label="Close"
					onClick={onClose}
				>
					<IonIcon slot="icon-only" icon={close} />
				</IonButton>
			</div>
		</IonHeader>
		<div className="event-alert-tnc-body ion-padding">
			<div className="tc-alert-info alert-info no-border">
				<CCRichText value={asHtml(termsHtml ?? "")} editable={false} />
			</div>
			<div className="tc-alert-container alert-container">
				<div className="tc-alert-button-list alert-button-list no-border">
					<span className="tc-alert-checkbox-container alert-checkbox-container">
						<IonCheckbox className="tc-alert-checkbox alert-checkbox" disabled />
						<IonText className="tc-alert-checkbox-label alert-checkbox-label">
							{TEXT.acceptTerms}
						</IonText>
					</span>
					<IonButton className="tc-alert-button alert-button" onClick={onClose}>
						{TEXT.save}
					</IonButton>
				</div>
			</div>
		</div>
	</IonModal>
);

interface ApprovalCardPreviewProps {
	/** The editor's draft, so unsaved edits are what gets drawn. */
	event: EventDoc;
}

export const ApprovalCardPreview: React.FC<ApprovalCardPreviewProps> = ({
	event,
}) => {
	/* Built from the draft on every render, so an edit on any tab is on the
	   card the next time it is opened. See `buildApprovalCardModel` for which
	   field feeds what. */
	const card = buildApprovalCardModel(event);

	return (
		<IonCard className="approval-card em-approval-preview">
				{card.past && (
					<div className="approval-card-status approval-card-status--none">
						<IonIcon icon={timeOutline} />
						<IonText>{TEXT.noResponse}</IonText>
					</div>
				)}
				<IonCardContent>
					<IonList>
						<IonItem lines="none">
							<IonBadge slot="start" color="warning">
								Sample
							</IonBadge>
							<IonText>
								{SAMPLE_STUDENT}, {SAMPLE_YEAR}
							</IonText>
						</IonItem>
						<div className="approval-card-summary">
							{card.imageUrl && (
								<div
									className="approval-card-image"
									style={
										{
											"--image-rows": card.locationLabel ? 3 : 2,
										} as React.CSSProperties
									}
								>
									<img src={card.imageUrl} alt="" loading="lazy" />
								</div>
							)}
							<div className="approval-card-summary-text">
								<IonItem lines="none">
									<IonText>{card.title}</IonText>
								</IonItem>
								{card.dateLabel && (
									<IonItem lines="none">
										<IonIcon icon={calendarOutline} color="danger" slot="start" />
										<IonText>{card.dateLabel}</IonText>
									</IonItem>
								)}
								{card.locationLabel && (
									<IonItem lines="none">
										<IonIcon icon={locationOutline} color="danger" slot="start" />
										<IonText>{card.locationLabel}</IonText>
									</IonItem>
								)}
							</div>
						</div>
						{card.descriptionHtml && (
							<IonItem lines="none">
								<div className="approval-card-description">
									<CCRichText
										value={asHtml(card.descriptionHtml)}
										editable={false}
									/>
								</div>
							</IonItem>
						)}
					</IonList>
					<div className="approval-card-actions">
						<IonButton
							className="approval-card-view ion-text-capitalize"
							size="small"
							fill="outline"
							disabled
						>
							{TEXT.view}
						</IonButton>
					</div>
				</IonCardContent>
		</IonCard>
	);
};
